import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  ArrowLeft, 
  Play, 
  Pause, 
  Volume2, 
  VolumeX, 
  Settings, 
  BookOpen,
  Trophy,
  Clock,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Target
} from 'lucide-react'
import Navigation from '../components/Navigation'
import { useVideoPlayer } from '../hooks/useVideoPlayer'
import { learningService } from '../services/learningService'
import { LearningVideo } from '../types/learning'

const VideoPlayerPage: React.FC = () => {
  const { videoId } = useParams<{ videoId: string }>()
  const navigate = useNavigate()
  const [video, setVideo] = useState<LearningVideo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showQuiz, setShowQuiz] = useState(false)
  const [showTranscript, setShowTranscript] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [relatedVideos, setRelatedVideos] = useState<LearningVideo[]>([])

  const {
    currentTime,
    duration,
    isPlaying,
    volume,
    playbackRate,
    progress,
    loading: playerLoading,
    error: playerError,
    completionPercentage,
    play,
    pause,
    seekTo,
    setVolume,
    setPlaybackRate,
    setCurrentTime,
    formatTime,
    timeRemaining
  } = useVideoPlayer(video, undefined) // TODO: Get userId from auth context

  useEffect(() => {
    const fetchVideo = async () => {
      if (!videoId) {
        setError('Video ID not provided')
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)
        
        const videoData = await learningService.getVideoById(videoId)
        if (!videoData) {
          setError('Video not found')
          return
        }

        setVideo(videoData)

        // Fetch related videos from the same module
        if (videoData.module_id) {
          const moduleVideos = await learningService.getModuleVideos(videoData.module_id)
          const related = moduleVideos.filter(v => v.id !== videoId).slice(0, 3)
          setRelatedVideos(related)
        }
      } catch (err) {
        console.error('Error fetching video:', err)
        setError(err instanceof Error ? err.message : 'Failed to load video')
      } finally {
        setLoading(false)
      }
    }

    fetchVideo()
  }, [videoId])

  const handleVideoEnd = () => {
    if (video && completionPercentage >= 90) {
      // Video completed - could trigger achievement check
      console.log('Video completed!')
    }
  }

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value)
    setCurrentTime(newTime)
    seekTo(newTime)
  }

  const togglePlayPause = () => {
    if (isPlaying) {
      pause()
    } else {
      play()
    }
  }

  const toggleMute = () => {
    setVolume(volume > 0 ? 0 : 1)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background-950">
        <Navigation />
        <div className="flex items-center justify-center min-h-[calc(100vh-80px)]">
          <div className="text-center">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
              className="w-16 h-16 border-4 border-primary-500/20 border-t-primary-500 rounded-full mx-auto mb-6"
            />
            <p className="text-background-400">Loading video...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !video) {
    return (
      <div className="min-h-screen bg-background-950">
        <Navigation />
        <div className="flex items-center justify-center min-h-[calc(100vh-80px)]">
          <div className="text-center">
            <p className="text-red-500 mb-4">{error || 'Video not found'}</p>
            <Link 
              to="/learn" 
              className="luxury-button primary"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Learning
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background-950">
      <Navigation />
      
      <div className="container mx-auto px-4 pt-24 pb-8">
        {/* Header */}
        <div className="mb-6">
          <Link 
            to={`/learn/${video.module_id}`} 
            className="inline-flex items-center text-primary-500 hover:text-primary-400 transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Module
          </Link>
          
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-background-100 mb-2">
                {video.title}
              </h1>
              <p className="text-background-400 mb-4">
                {video.description}
              </p>
              
              <div className="flex items-center space-x-6 text-sm text-background-500">
                <div className="flex items-center">
                  <Clock className="w-4 h-4 mr-1" />
                  {formatTime(duration)}
                </div>
                <div className="flex items-center">
                  <Target className="w-4 h-4 mr-1" />
                  {video.difficulty}
                </div>
                <div className="flex items-center">
                  <Trophy className="w-4 h-4 mr-1" />
                  {video.xp_reward} XP
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowTranscript(!showTranscript)}
                className={`luxury-button ${showTranscript ? 'primary' : 'secondary'}`}
              >
                <BookOpen className="w-4 h-4 mr-2" />
                Transcript
              </button>
              
              <button
                onClick={() => setShowQuiz(true)}
                className="luxury-button primary"
              >
                Take Quiz
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Video Player */}
          <div className="lg:col-span-3">
            <div className="luxury-card p-6">
              {/* YouTube Embed */}
              <div className="relative aspect-video bg-background-900 rounded-lg overflow-hidden mb-4">
                <iframe
                  src={`https://www.youtube.com/embed/${video.video_embed_id}?autoplay=0&rel=0&modestbranding=1`}
                  title={video.title}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>

              {/* Custom Video Controls */}
              <div className="space-y-4">
                {/* Progress Bar */}
                <div className="space-y-2">
                  <div className="flex justify-between text-sm text-background-500">
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration)}</span>
                  </div>
                  
                  <div className="relative">
                    <input
                      type="range"
                      min="0"
                      max={duration || 0}
                      value={currentTime}
                      onChange={handleSeek}
                      className="w-full h-2 bg-background-700 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div 
                      className="absolute top-0 left-0 h-2 bg-primary-500 rounded-lg"
                      style={{ width: `${completionPercentage}%` }}
                    />
                  </div>
                </div>

                {/* Control Buttons */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={togglePlayPause}
                      className="luxury-button primary p-3"
                    >
                      {isPlaying ? (
                        <Pause className="w-5 h-5" />
                      ) : (
                        <Play className="w-5 h-5" />
                      )}
                    </button>

                    <button
                      onClick={toggleMute}
                      className="luxury-button secondary p-3"
                    >
                      {volume > 0 ? (
                        <Volume2 className="w-5 h-5" />
                      ) : (
                        <VolumeX className="w-5 h-5" />
                      )}
                    </button>

                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-background-500">Speed:</span>
                      <select
                        value={playbackRate}
                        onChange={(e) => setPlaybackRate(parseFloat(e.target.value))}
                        className="bg-background-800 border border-background-700 rounded px-3 py-1 text-background-100"
                      >
                        <option value={0.5}>0.5x</option>
                        <option value={0.75}>0.75x</option>
                        <option value={1}>1x</option>
                        <option value={1.25}>1.25x</option>
                        <option value={1.5}>1.5x</option>
                        <option value={2}>2x</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    {progress && (
                      <div className="text-sm text-background-500">
                        {progress.completion_percentage}% complete
                      </div>
                    )}
                    
                    {completionPercentage >= 90 && (
                      <div className="flex items-center text-green-500">
                        <CheckCircle className="w-5 h-5 mr-1" />
                        <span className="text-sm font-medium">Completed!</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Learning Objectives */}
              {video.learning_objectives.length > 0 && (
                <div className="mt-6 pt-6 border-t border-background-800">
                  <h3 className="text-lg font-semibold text-background-100 mb-3">
                    Learning Objectives
                  </h3>
                  <ul className="space-y-2">
                    {video.learning_objectives.map((objective, index) => (
                      <li key={index} className="flex items-start">
                        <Target className="w-4 h-4 text-primary-500 mt-1 mr-3 flex-shrink-0" />
                        <span className="text-background-300">{objective}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Transcript */}
              <AnimatePresence>
                {showTranscript && video.transcript && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-6 pt-6 border-t border-background-800"
                  >
                    <h3 className="text-lg font-semibold text-background-100 mb-3">
                      Video Transcript
                    </h3>
                    <div className="bg-background-800 rounded-lg p-4 max-h-64 overflow-y-auto">
                      <p className="text-background-300 whitespace-pre-wrap">
                        {video.transcript}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Progress Card */}
            {progress && (
              <div className="luxury-card p-4">
                <h3 className="text-lg font-semibold text-background-100 mb-3">
                  Your Progress
                </h3>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-background-500">Watched</span>
                      <span className="text-background-300">
                        {formatTime(progress.watched_seconds)} / {formatTime(video.duration || 0)}
                      </span>
                    </div>
                    <div className="w-full bg-background-700 rounded-full h-2">
                      <div 
                        className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${progress.completion_percentage}%` }}
                      />
                    </div>
                  </div>
                  
                  {progress.completed && (
                    <div className="flex items-center text-green-500">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      <span className="text-sm font-medium">Video Completed!</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Related Videos */}
            {relatedVideos.length > 0 && (
              <div className="luxury-card p-4">
                <h3 className="text-lg font-semibold text-background-100 mb-3">
                  More in this Module
                </h3>
                <div className="space-y-3">
                  {relatedVideos.map((relatedVideo) => (
                    <Link
                      key={relatedVideo.id}
                      to={`/learn/video/${relatedVideo.id}`}
                      className="block p-3 bg-background-800 rounded-lg hover:bg-background-700 transition-colors"
                    >
                      <h4 className="text-sm font-medium text-background-100 mb-1 line-clamp-2">
                        {relatedVideo.title}
                      </h4>
                      <div className="flex items-center justify-between text-xs text-background-500">
                        <span>{formatTime(relatedVideo.duration || 0)}</span>
                        <span>{relatedVideo.xp_reward} XP</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="luxury-card p-4">
              <h3 className="text-lg font-semibold text-background-100 mb-3">
                Navigation
              </h3>
              <div className="space-y-2">
                <Link
                  to={`/learn/${video.module_id}`}
                  className="flex items-center justify-between p-2 bg-background-800 rounded-lg hover:bg-background-700 transition-colors"
                >
                  <span className="text-sm text-background-300">Module Overview</span>
                  <ChevronRight className="w-4 h-4 text-background-500" />
                </Link>
                
                <button
                  onClick={() => setShowQuiz(true)}
                  className="w-full flex items-center justify-between p-2 bg-primary-500/20 rounded-lg hover:bg-primary-500/30 transition-colors"
                >
                  <span className="text-sm text-primary-300">Take Quiz</span>
                  <ChevronRight className="w-4 h-4 text-primary-500" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quiz Modal */}
      <AnimatePresence>
        {showQuiz && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowQuiz(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-background-900 rounded-xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-background-100">
                  Quiz: {video.title}
                </h2>
                <button
                  onClick={() => setShowQuiz(false)}
                  className="text-background-500 hover:text-background-300"
                >
                  ×
                </button>
              </div>
              
              <p className="text-background-400 mb-6">
                Test your knowledge with this quiz about the video content.
              </p>
              
              <div className="text-center">
                <p className="text-background-500 mb-4">
                  Quiz functionality will be implemented in the next step.
                </p>
                <button
                  onClick={() => setShowQuiz(false)}
                  className="luxury-button primary"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default VideoPlayerPage
